import React, { useState, useEffect } from 'react';
import { TrashIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import Alert from '../../global/Alert/Alert.tsx';
import { encodeData } from '../../utils/encodeUtils.ts';
import SharedButton from '../../global/Button/Button.tsx';
import { userAppContext } from '../../Context/Context';
import { getFilters, deleteFilter } from '../../services/savedFiltersService';
import { updateFilter } from '../../services/savedFiltersService';

const ProfileFavorites = ({ favoritesClass }) => {
  const [savedSearches, setSavedSearches] = useState([]);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);

  const { isAuthenticated } = userAppContext();

  const operatorMap = {
    eq: '=',
    neq: '≠',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
    in: '∈',
    nin: '∉',
    contains: 'contains',
    startswith: 'starts with',
    endswith: 'ends with',
  };

  useEffect(() => {
    if (isAuthenticated) {
      loadSavedSearches();
    }
  }, [isAuthenticated]);

  const loadSavedSearches = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getFilters();
      const cloudFilters = response.filters || [];

      const searches = cloudFilters.map(filter => ({
        id: filter.filterId,
        table: filter.isEvidence ? 'Evidence' : 'Summary',
        filters: filter.filters,
        searchQuery: filter.searchQuery,
        savedAt: filter.createdAt,
        name: filter.name,
      }));

      setSavedSearches(searches);
    } catch (err) {
      console.error('Error loading saved searches:', err);
      setError('Failed to load saved searches');
    } finally {
      setIsLoading(false);
    }
  };

  const buildSearchUrl = search => {
    try {
      const url = new URL(`${window.location.origin}/search`);

      if (search.searchQuery) {
        const searchParams = new URLSearchParams(search.searchQuery);
        searchParams.forEach((value, key) => {
          url.searchParams.set(key, value);
        });

        const filters = search.filters || [];
        if (filters.length > 0) {
          const encodedFilters = encodeData(filters);
          url.searchParams.set('f', encodedFilters);
        }

        return url.href;
      }

      if (search.searchQuery) {
        const searchParams = new URLSearchParams(search.searchQuery);
        searchParams.forEach((value, key) => {
          url.searchParams.set(key, value);
        });

        const filters = search.filters || [];
        if (filters.length > 0) {
          const encodedFilters = encodeData(filters);
          url.searchParams.set('f', encodedFilters);
        }

        return url.href;
      }

      const filters = search.filters || [];
      if (filters.length === 0) return null;

      const viewFilter = filters.find(f => f.name === 'view');
      const tabFilter = filters.find(f => f.name === 'tab');
      const sourceFilter = filters.find(f => f.name === 'source');
      const identsFilter = filters.find(f => f.name === 'idents');

      url.searchParams.set('view', viewFilter?.value || 'ALL');
      url.searchParams.set('tab', tabFilter?.value || 'GDA');
      url.searchParams.set('source', sourceFilter?.value || 'ALL');
      url.searchParams.set('idents', identsFilter?.value || 'ALL');

      const columnFilters = filters.filter(
        f =>
          !['view', 'tab', 'source', 'idents'].includes(f.name) &&
          f.value !== undefined &&
          f.value !== null &&
          f.value !== '' &&
          !(Array.isArray(f.value) && f.value.length === 0),
      );

      if (columnFilters.length > 0) {
        const encodedFilters = encodeData(columnFilters);
        url.searchParams.set('f', encodedFilters);
      }

      if (columnFilters.length > 0) {
        const encodedFilters = encodeData(columnFilters);
        url.searchParams.set('f', encodedFilters);
      }

      return url.href;
    } catch (error) {
      console.error('Error building search URL:', error);
      return null;
    }
  };

  const handleNavigateToSearch = search => {
    if (search.searchQuery) {
      const targetUrl = buildSearchUrl(search);
      if (targetUrl) {
        const relativePath = targetUrl.replace(window.location.origin, '');
        window.open(relativePath, '_blank');
      }
      return;
    }

    const activeFilters = (search.filters || []).map(f => ({
      ...f,
      isEvidence: search.table === 'Evidence',
    }));
    if (search.searchQuery) {
      const targetUrl = buildSearchUrl(search);
      if (targetUrl) {
        const relativePath = targetUrl.replace(window.location.origin, '');
        window.open(relativePath, '_blank');
      }
      return;
    }

    const targetUrl = buildSearchUrl({ ...search, filters: activeFilters });
    if (targetUrl) {
      const relativePath = targetUrl.replace(window.location.origin, '');
      window.open(relativePath, '_blank');
    }
  };

  const handleDeleteSearch = async searchId => {
    try {
      await deleteFilter(searchId);

      const updatedSearches = savedSearches.filter(s => s.id !== searchId);
      setSavedSearches(updatedSearches);

      setShowDeleteAlert(true);
      setTimeout(() => setShowDeleteAlert(false), 3000);
    } catch (error) {
      console.error('Error deleting search:', error);
      setError('Failed to delete search');
    }
  };

  const handleClearAllSearches = async () => {
    try {
      await Promise.all(savedSearches.map(search => deleteFilter(search.id)));

      setSavedSearches([]);
      setShowDeleteAlert(true);
      setTimeout(() => setShowDeleteAlert(false), 3000);
    } catch (error) {
      console.error('Error clearing all searches:', error);
      setError('Failed to clear all searches');
    }
  };

  const getSearchDetails = filters => {
    if (!Array.isArray(filters) || filters.length === 0) {
      return { filters: 'No column filters applied' };
    }

    const contextParams = ['view', 'tab', 'source', 'idents'];
    const columnFilters = filters.filter(f => !contextParams.includes(f.name));

    if (columnFilters.length === 0) {
      return { filters: 'No column filters applied' };
    }

    if (columnFilters.length === 0) {
      return { filters: 'No column filters applied' };
    }

    const filtersStr = columnFilters
      .filter(f => f.value && f.value !== '' && !(Array.isArray(f.value) && f.value.length === 0))
      .map(f => {
        const label = f.label || f.name || '';
        const value = Array.isArray(f.value) ? f.value.join(', ') : f.value;
        const operator = f.operator ? ` ${operatorMap[f.operator] || f.operator} ` : ': ';
        return `${label}${operator}${value}`;
      })
      .join(', ');

    return { filters: filtersStr || 'No column filters applied' };
  };

  const formatDate = timestamp => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAuthenticated) {
    return (
      <div className={favoritesClass} id="favorites">
        <h2>Saved Searches</h2>
        <div className="wrap-input100" />
        <div style={{ padding: '20px 0' }}>
          <p className="label-input100" style={{ color: '#999' }}>
            Please log in to see your saved searches.
          </p>
        </div>
      </div>
    );
  }

  const handleSaveEdit = async filterId => {
    if (!editName.trim()) return;

    try {
      await updateFilter(filterId, { name: editName });

      setSavedSearches(prev => prev.map(s => (s.id === filterId ? { ...s, name: editName } : s)));

      setEditId(null);
      setEditName('');
      setShowUpdateAlert(true);
      setTimeout(() => setShowUpdateAlert(false), 3000);
    } catch (error) {
      console.error('Error updating filter name:', error);
      setError('Failed to update filter name');
    }
  };

  return (
    <>
      {showDeleteAlert && <Alert message="Search deleted successfully" type="success" />}
      {showUpdateAlert && <Alert message="Filter saved successfully!" type="success" />}
      {error && <Alert message={error} type="error" />}

      <div className={favoritesClass} id="favorites">
        <h2>Saved Searches</h2>

        {/* Aviso de privacidad */}
        <div
          style={{
            background: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '6px',
            padding: '12px',
            marginBottom: '16px',
            fontSize: '13px',
          }}
        >
          <strong style={{ color: '#0369a1' }}>🔒 Your privacy is protected:</strong>
          <p style={{ margin: '4px 0 0 0', color: '#075985' }}>
            All your saved searches are encrypted end-to-end. Only you can see your filters—not even our team
            can access them.
          </p>
        </div>

        <div className="wrap-input100" />

        {isLoading ? (
          <div style={{ padding: '40px 0', textAlign: 'center' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden"></span>
            </div>
            <p style={{ marginTop: '16px', color: '#666' }}>Loading your saved searches...</p>
          </div>
        ) : savedSearches.length === 0 ? (
          <div style={{ padding: '20px 0' }}>
            <p className="label-input100" style={{ color: '#999' }}>
              You don&apos;t have any saved searches yet. Apply filters in the grid and save them to see them
              here.
            </p>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <p className="input100">Total saved searches: {savedSearches.length}</p>
              {savedSearches.length > 0 && (
                <SharedButton
                  id="clearAll"
                  size="xs"
                  onClick={handleClearAllSearches}
                  type="button"
                  className="BtnAccion mx-0 mb-2"
                  style={{ height: '30px', width: '10%', fontSize: '12px' }}
                >
                  Clear All
                </SharedButton>
              )}
            </div>

            <div style={{ marginTop: '20px' }}>
              {savedSearches.map(search => (
                <div key={search.id} className="saved-filter-card mb-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '8px' }}>
                        {editId === search.id ? (
                          <input
                            type="text"
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') handleSaveEdit(search.id);
                            }}
                            style={{
                              fontSize: '16px',
                              padding: '2px 6px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              width: '70%',
                            }}
                            autoFocus
                          />
                        ) : (
                          <span className="text-pink font-bold" style={{ fontSize: '16px' }}>
                            {search.name}
                          </span>
                        )}

                        <span style={{ marginLeft: '12px', fontSize: '12px', color: '#666' }}>
                          {formatDate(search.savedAt)}
                        </span>
                      </div>
                      <div style={{ marginBottom: '4px' }}>
                        <span
                          style={{
                            background: search.table === 'Evidence' ? '#dbeafe' : '#fce7f3',
                            color: search.table === 'Evidence' ? '#1e40af' : '#9f1239',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: '500',
                          }}
                        >
                          {search.table}
                        </span>
                      </div>
                      <div style={{ marginTop: '8px' }}>
                        {(() => {
                          const d = getSearchDetails(search.filters);
                          return (
                            <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>
                              <strong>Column filters:</strong> {d.filters}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginLeft: '16px' }}>
                      {editId === search.id ? (
                        <>
                          <SharedButton
                            size="xs"
                            type="button"
                            onClick={() => handleSaveEdit(search.id)}
                            disabled={!editName.trim()}
                          >
                            Save
                          </SharedButton>
                          <SharedButton
                            size="xs"
                            type="button"
                            variant="secondary"
                            onClick={() => {
                              setEditId(null);
                              setEditName('');
                            }}
                          >
                            Cancel
                          </SharedButton>
                        </>
                      ) : (
                        <>
                          <SharedButton
                            size="xs"
                            type="button"
                            onClick={() => {
                              setEditId(search.id);
                              setEditName(search.name);
                            }}
                          >
                            Edit
                          </SharedButton>
                          <SharedButton
                            size="xs"
                            type="button"
                            onClick={() => handleNavigateToSearch(search)}
                          >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                            Open
                          </SharedButton>
                          <SharedButton size="xs" type="button" onClick={() => handleDeleteSearch(search.id)}>
                            <TrashIcon className="h-4 w-4" />
                          </SharedButton>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ProfileFavorites;
